import { type SyntheticEvent, useEffect, useState } from 'react'
import type { CoachingPlanContact, CoachingPlan } from '../../../../../models/CoachingPlan'
import { Modal } from '../../../../customComponents/Modal'
import { Box, TextField, InputAdornment, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Typography, IconButton, Stack, Alert } from '@mui/material'
import { type FieldError } from '../../../../../hooks/use-fetch'
import { toast } from 'react-toastify'
import { AddCoachingPlan, UpdateCoachingPlan, GetDistrictsFromISDPlanOrgId } from '../../../../../services/AnnualPlanService'
import { AddCircle } from '@mui/icons-material'
import MUIAutoComplete from '../../../../customComponents/MUIAutoComplete'
import type { SelectableStringOption } from '../../../../../utils/genericTypes'
import { ActivityEditSession } from '../ActivityEditSession'
import { PriorityTwoActivityName } from '../../../../../models/PriorityTwoActivityName'
import type React from 'react'
import { isValidPositiveNumber } from '../../../../../utils/isValidNumber'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { BudgetThresholdExceededModal } from '../../BudgetExceededModal'
import { budgetNeedsReview } from '../../../../../utils/budgetUtil'
import { handleApiResponse } from '../../../../../utils/apiHelpers'

export interface AdditionalDetailsFormValues {
  notes: string
}

interface Props {
  priorityId: number
  editCoachingPlan?: CoachingPlan
  planOrgId: number
  orgName: string
  onClose: (refresh: boolean) => void
  implementationPhase?: boolean
  startOnDetailsPage?: boolean
}

type ConfirmState = 'none' | 'needsConfirmation' | 'confirmed'

export function AddCoachingPlanModal (props: Props): JSX.Element {
  const DISTRICT = 'District'
  const ISD = 'ISD'
  const editOrgName = props.editCoachingPlan?.organizationName ?? ''
  const [confirmState, setConfirmState] = useState<ConfirmState>('none')
  const [isLoading, setIsLoading] = useState(true)
  const [orgType, setOrgType] = useState(props.editCoachingPlan?.organizationType ?? ISD)
  const [contacts, setContacts] = useState<CoachingPlanContact[]>(props.editCoachingPlan?.contacts ?? [{ contactEmail: '', contactName: '' }])
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([])
  const [selectedDistrict, setSelectedDistrict] = useState<SelectableStringOption | null>(editOrgName !== ''
    ? { id: editOrgName, name: editOrgName }
    : null)
  const [districtOptions, setDistrictOptions] = useState<SelectableStringOption[]>([])
  const [formValues, setFormValues] = useState<CoachingPlan>(props.editCoachingPlan ?? {
    actions: '',
    budgetRequest: '',
    coachingPlanId: undefined,
    contacts,
    organizationName: '',
    organizationType: ISD,
    priorityId: undefined,
    resources: '',
    needsReview: false
  })
  const [modalPage, setModalPage] = useState<'edit' | 'details'>(props.startOnDetailsPage === true ? 'details' : 'edit')
  const [additionalDetailsFormValues, setAdditionalDetailsFormValues] = useState<AdditionalDetailsFormValues>({
    notes: props.editCoachingPlan?.notes ?? ''
  })
  const [additionalDetailsSubmitted, setAdditionalDetailsSubmitted] = useState(false)
  const [showCancelUpdatesModal, setShowCancelUpdatesModal] = useState(false)

  const additionalDetailsFormValidators: Record<keyof AdditionalDetailsFormValues, { required: boolean, isValid: boolean }> = {
    notes: { required: true, isValid: additionalDetailsFormValues.notes.trim() !== '' }
  }
  const additionalDetailsHasInvalidFields = Object.values(additionalDetailsFormValidators).some(v => v.required && !v.isValid)

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      await fetchOrgOptions()
      setIsLoading(false)
    }

    void fetchData()
  }, [])

  const fetchOrgOptions = async (): Promise<void> => {
    const options = await handleApiResponse(GetDistrictsFromISDPlanOrgId(props.planOrgId))
    if (options != null) setDistrictOptions(options.map(o => ({ id: o.name, name: o.name })))
  }

  const selectDistrict = (e: SyntheticEvent, value: { id: string | number, name: string } | null): void => {
    setSelectedDistrict(value as SelectableStringOption)
  }

  const handleFieldUpdate = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setFormValues({
      ...formValues,
      [e.target.name]: e.target.value
    })
  }

  const addContact = (): void => {
    const contactExists: boolean = contacts.some(c => c.contactEmail !== '' || c.contactName !== '')
    let formFieldErrors = [...fieldErrors]
    if (!contactExists) {
      if (!formFieldErrors.some(ffe => ffe.fieldName === '0contactName') && contacts[contacts.length - 1].contactName === '') {
        formFieldErrors.push({ fieldName: '0contactName', message: '' })
      }

      if (!formFieldErrors.some(ffe => ffe.fieldName === '0contactEmail') && contacts[contacts.length - 1].contactEmail === '') {
        formFieldErrors.push({ fieldName: '0contactEmail', message: '' })
      }

      setFieldErrors(formFieldErrors)
      return
    } else {
      if (formFieldErrors.some(ffe => ffe.fieldName === '0contactName')) {
        formFieldErrors = formFieldErrors.filter(f => f.fieldName !== '0contactName')
      }

      if (formFieldErrors.some(ffe => ffe.fieldName === '0contactEmail')) {
        formFieldErrors = formFieldErrors.filter(f => f.fieldName !== '0contactEmail')
      }
      setFieldErrors(formFieldErrors)
    }

    if (contacts[contacts.length - 1].contactEmail === '' || contacts[contacts.length - 1].contactName === '') {
      return
    }

    setContacts([...contacts, { contactEmail: '', contactName: '' }])
  }

  const handleContactEmailUpdate = (value: string, index: number): void => {
    const tempContacts = [...contacts]
    const contact: CoachingPlanContact = tempContacts[index]
    contact.contactEmail = value
    tempContacts[index] = contact
    setContacts(tempContacts)
  }

  const handleContactNameUpdate = (value: string, index: number): void => {
    const tempContacts = [...contacts]
    const contact: CoachingPlanContact = tempContacts[index]
    contact.contactName = value
    tempContacts[index] = contact
    setContacts(tempContacts)
  }

  const handleAdditionalDetailsFieldUpdate = (e: { target: { name: string, value: string } }): void => {
    const { name, value } = e.target
    setAdditionalDetailsFormValues({ ...additionalDetailsFormValues, [name]: value })
  }

  function validateForm (): boolean {
    const formFieldErrors: FieldError[] = []

    if (formValues.actions === '') {
      formFieldErrors.push({ fieldName: 'actions', message: '' })
    }

    if (formValues.resources === '') {
      formFieldErrors.push({ fieldName: 'resources', message: '' })
    }

    if (orgType === DISTRICT && selectedDistrict == null) {
      formFieldErrors.push({ fieldName: 'districtSelect', message: '' })
    }

    const contactExists: boolean = contacts.some(c => c.contactEmail !== '' || c.contactName !== '')
    if (!contactExists) {
      if (contacts[0].contactName === '') {
        formFieldErrors.push({ fieldName: '0contactName', message: '' })
      }

      if (contacts[0].contactEmail === '') {
        formFieldErrors.push({ fieldName: '0contactEmail', message: '' })
      }
    }

    if (!isValidPositiveNumber(formValues.budgetRequest)) {
      formFieldErrors.push({ fieldName: 'budgetRequest', message: 'Budget Request must be a positive number' })
    }

    setFieldErrors(formFieldErrors)
    if (formFieldErrors.length > 0) {
      return false
    }

    return true
  }

  const handleConfirm = async (): Promise<void> => {
    if (props.implementationPhase === true) {
      await handleImplementationPhaseSave()
    } else {
      await handleContractPhaseSave()
    }
  }

  const handleImplementationPhaseSave = async (): Promise<void> => {
    if (props.editCoachingPlan != null && modalPage === 'edit' && props.editCoachingPlan.isUpdate === false) {
      if (budgetNeedsReview(props.editCoachingPlan.budgetRequest, formValues.budgetRequest)) {
        setConfirmState('needsConfirmation')
        return
      }
    }

    await continueSubmit()
  }

  const continueSubmit = async (): Promise<void> => {
    if (modalPage === 'edit') {
      if (!validateForm()) {
        toast.error('Please fill out all required fields')
        return
      }
      setModalPage('details')
    } else if (modalPage === 'details') {
      setAdditionalDetailsSubmitted(true)
      if (additionalDetailsHasInvalidFields) {
        toast.error('Please fill out all required fields')
        return
      }

      const success = await AddUpdateCoachingPlan()

      if (success) toast.success('Coaching Plan Updated')

      props.onClose(true)
    }
  }

  const getCompletedForm = (isImplementationPhase: boolean): CoachingPlan => {
    const completedForm: CoachingPlan = { ...formValues }
    completedForm.contacts = contacts.filter(c => c.contactEmail !== '' && c.contactName !== '')
    completedForm.priorityId = props.priorityId
    completedForm.organizationType = orgType
    completedForm.notes = props.editCoachingPlan?.notes ?? null
    completedForm.progress = props.editCoachingPlan?.progress ?? null
    completedForm.progressNotes = props.editCoachingPlan?.progressNotes ?? null
    completedForm.implementationNotes = props.editCoachingPlan?.implementationNotes ?? null
    completedForm.budgetSpent = props.editCoachingPlan?.budgetSpent ?? null

    if (selectedDistrict != null) {
      completedForm.organizationName = selectedDistrict.name
    } else {
      completedForm.organizationName = props.orgName
    }

    completedForm.isImplementationPhase = isImplementationPhase
    completedForm.isUpdate = props.editCoachingPlan?.isUpdate ?? false
    if (isImplementationPhase) {
      completedForm.notes = additionalDetailsFormValues.notes
    }

    return completedForm
  }

  const handleContractPhaseSave = async (): Promise<void> => {
    if (!validateForm()) {
      toast.error('Please fill out all required fields')
      return
    }

    const success = await AddUpdateCoachingPlan()

    if (success) toast.success('Coaching Plan saved.')

    props.onClose(true)
  }

  const AddUpdateCoachingPlan = async (): Promise<boolean> => {
    const completedForm = getCompletedForm(props.implementationPhase === true)

    const hasCoachingPlanId = completedForm.coachingPlanId != null

    const { success, errors } = hasCoachingPlanId
      ? await UpdateCoachingPlan(completedForm)
      : await AddCoachingPlan(completedForm)

    for (const error of errors) toast.error(error)

    return success
  }

  if (isLoading) return <></>

  const getTitle = (): string => {
    if (props.implementationPhase === true && props.editCoachingPlan != null) return 'Update Coaching Plan'
    return props.editCoachingPlan != null ? 'Edit Coaching Plan' : 'Add Coaching Plan'
  }

  const handleClose = async (): Promise<void> => {
    if (props.implementationPhase === true && modalPage === 'details') {
      setShowCancelUpdatesModal(true)
      return
    }

    props.onClose(false)
  }
  const handleCancel = (): void => {
    if (props.implementationPhase === true && modalPage === 'details') {
      setModalPage('edit')
      return
    }

    props.onClose(false)
  }

  const handleConfirmCancelUpdates = async (): Promise<void> => {
    // save partial updates
    const success = await AddUpdateCoachingPlan()
    if (success) toast.success('Coaching Plan Partially Updated')
    // close modal
    props.onClose(true)
  }

  if (showCancelUpdatesModal) {
    return (
      <Modal
        open={true}
        title='Cancel Updates'
        cancelButtonText='Go Back'
        confirmButtonText='Confirm Cancel'
        data-testid='confirmCancelUpdatesModal'
        bodyContent={<Stack gap='1rem' m='24px 0'>
          <Typography variant='body1' fontWeight={600}>Are you sure you want to cancel?</Typography>
          <Typography variant='body1'>If you proceed your changes in the Additional Details section will not be saved.</Typography>
        </Stack>}
        onClose={() => { setShowCancelUpdatesModal(false) }}
        onConfirm={handleConfirmCancelUpdates}
        maxWidth='xs'
      />
    )
  }

  return (confirmState === 'needsConfirmation'
    ? <BudgetThresholdExceededModal
        onClose={() => {
          setConfirmState('none')
          props.onClose(props.editCoachingPlan?.isUpdate ?? false)
        }}
        onConfirm={() => {
          setConfirmState('confirmed')
          void continueSubmit()
        }}
    />
    : <Modal
        open={true}
        data-testid='addCoachingPlanModal'
        maxWidth='md'
        title={getTitle()}
        confirmButtonText={props.implementationPhase === true && modalPage === 'edit' ? 'Save & Continue' : 'Save'}
        cancelButtonText={props.implementationPhase === true && modalPage === 'details' ? 'Go Back' : 'Cancel'}
        onClose={handleClose}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        bodyContent={
          <Stack gap='1rem' py='24px'>
            {modalPage === 'edit' && (
              <Box minWidth='200px' >
                {
                  props.implementationPhase === true && props.editCoachingPlan == null &&
                    <Alert data-testid='contributorAlert' icon={<InfoOutlinedIcon fontSize="inherit" sx={{ fill: 'white' }} />} sx={{
                      backgroundColor: 'var(--blue-500)',
                      color: 'white',
                      mb: '.5em',
                      fill: 'white'
                    }} severity="info"
                    >The addition of this item will prompt a review from the START team.
                    </Alert>
                }
                <Typography variant='body1' sx={{ my: '.5em' }}>Location</Typography>
                <FormControl
                  sx={{ my: '.5em', alignContent: 'center', width: '100%' }}
                  data-testid='formControlOrgRadio'
                  required={true}
                  error={fieldErrors.some((error) => error.fieldName === 'orgTypeRadio')}
                >
                  <FormLabel>Is this for an ISD or a District?</FormLabel>
                  <RadioGroup
                    row={true}
                    aria-labelledby="scope-radio-buttons-group"
                    name="orgTypeRadio"
                    value={orgType}
                    onChange={(e, value) => { setOrgType(value) }}
                  >
                    <FormControlLabel data-testid='isdRadio' value="ISD" control={<Radio />} label="ISD" />
                    <FormControlLabel data-testid='districtRadio' value="District" control={<Radio />} label="District" />
                  </RadioGroup>
                </FormControl>

                <Typography variant='body1'>{orgType}</Typography>

                {orgType === DISTRICT &&
                  <MUIAutoComplete
                    dataTestId='districtSelect'
                    label='District'
                    name='districtSelect'
                    options={districtOptions}
                    value={selectedDistrict ?? undefined}
                    sx={{ mt: '1em' }}
                    onChange={selectDistrict}
                    required
                    error={fieldErrors.some((error) => error.fieldName === 'districtSelect')}
                  />
            }

                {contacts.map((contact, index) => {
                  return <Box key={`${index}contactContainer`}>
                    <TextField
                      sx={{ my: '.5em' }}
                      data-testid={`${index}contactName`}
                      fullWidth
                      value={contact.contactName}
                      onChange={(e) => { handleContactNameUpdate(e.target.value, index) }}
                      placeholder='Contact Name'
                      label={'Contact Name'}
                      name={`${index}contactName`}
                      required
                      variant='filled'
                      error={fieldErrors.some((error) => error.fieldName === `${index}contactName`)}
                    />

                    <TextField
                      sx={{ my: '.5em' }}
                      data-testid={`${index}contactEmail`}
                      fullWidth
                      value={contact.contactEmail}
                      onChange={(e) => { handleContactEmailUpdate(e.target.value, index) }}
                      placeholder='Contact Email'
                      label={'Contact Email'}
                      name={`${index}contactEmail`}
                      required
                      variant='filled'
                      error={fieldErrors.some((error) => error.fieldName === `${index}contactEmail`)}
                    />
                  </Box>
                })}

                <Typography variant='body1'>Add Additional Contact <IconButton data-testid='addButton' onClick={addContact}><AddCircle sx={{ color: 'var(--grey-700)' }} /></IconButton></Typography>

                <TextField
                  sx={{ my: '.5em' }}
                  data-testid='actions'
                  fullWidth
                  value={formValues.actions}
                  onChange={handleFieldUpdate}
                  placeholder='What actions are planned?'
                  label='What actions are planned?'
                  name='actions'
                  required
                  variant='filled'
                  error={fieldErrors.some((error) => error.fieldName === 'actions')}
                />

                <TextField
                  sx={{ my: '.5em' }}
                  data-testid='resources'
                  fullWidth
                  value={formValues.resources}
                  onChange={handleFieldUpdate}
                  placeholder='What resources are needed?'
                  label={'What resources are needed?'}
                  name='resources'
                  required
                  variant='filled'
                  error={fieldErrors.some((error) => error.fieldName === 'resources')}
                />

                <TextField
                  sx={{ mt: '.75em', width: '250px' }}
                  onChange={handleFieldUpdate}
                  label='Budget Request'
                  data-testid='budgetRequest'
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  name='budgetRequest'
                  required
                  variant='filled'
                  error={fieldErrors.some((error) => error.fieldName === 'budgetRequest')}
                  value={formValues.budgetRequest}
                />

                {props.editCoachingPlan != null &&
                  <ActivityEditSession
                    activityId={props.editCoachingPlan.coachingPlanId ?? 0}
                    activityName={PriorityTwoActivityName.CoachingStructureAndSystems}
                    onClose={() => { props.onClose(false) }}
                  />
            }
              </Box>
            )}

            {modalPage === 'details' && (
              <>
                <Typography variant='h4'>Additional Details</Typography>

                <FormControl
                  required={additionalDetailsFormValidators.notes.required}
                  error={additionalDetailsSubmitted && !additionalDetailsFormValidators.notes.isValid}
                  variant='filled'
                  sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
                >
                  <FormLabel sx={{ display: 'flex' }}>
                    <Typography
                      variant='h5'
                      sx={{
                        color: theme => additionalDetailsSubmitted && !additionalDetailsFormValidators.notes.isValid ? theme.palette.error.main : 'inherit'
                      }}
                    >
                      Notes
                    </Typography>
                  </FormLabel>

                  <TextField
                    hiddenLabel
                    fullWidth
                    sx={{ minWidth: '400px', borderRadius: '6px' }}
                    multiline
                    id='notes'
                    name='notes'
                    data-testid='notes'
                    rows={3}
                    placeholder={'Provide any further information related to your Coaching Plan.'}
                    value={additionalDetailsFormValues.notes}
                    onChange={handleAdditionalDetailsFieldUpdate}
                  />
                </FormControl>
              </>
            )}
          </Stack>
        }
    />)
}
